import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Card, Tabs, Divider, Col, Row, Spin, Modal } from 'antd';
import Button from '@mui/material/Button';
import TickerSearch from '../../components/TickerSearch/TickerSearch';
import StockChart from "../../charts/StockChart";
import PerformanceHistory from './Views/PerformanceHistory';
import Risk from './Views/Risk';
import {
    prepareOverviewChartData
} from './utils';
import ItemController from "./components/ItemController";
import ItemMenu from "./components/ItemMenu";
import PortfolioSelector from "../Portfolio/PortfolioSelectors/PortfolioSelector";
import HistoricalVolatility from "./Views/HistoricalVolatility";
import ReturnsDistributions from "./Views/ReturnsDistributions";
import * as PropTypes from "prop-types";
import Placeholder from "../../components/Placeholder";
import GeneralStockChart from "../../SuperComponents/StockCharts/GeneralStockChart";
import {useRelativeMetrics} from "../../SuperComponents/StockCharts/useRelativeMetrics";
import RelativeMetricToolbar from "../../SuperComponents/StockCharts/RelativeMetricToolbar";
import { useScreenHeight } from "../../hooks/useScreen";
import usePerformanceComparison from "./usePerformanceComparison";
import NavTrail from "../../layouts/NavTrail";


const { TabPane } = Tabs;

const tabKeyToLabelMap = {
  "1": "Overview",
  "2": "Returns",
  "3": "Risk",
  "6": "Volatility",
  "7": "Distributions",
  "8": "Metrics",
};




/**
 *
 * @param initialObjects {Array[Object[{id, label}]], optional} - An array of objects containing id and label of the initial
 *                                                                ...timeseries portfolios/securities to load
 * @param isChildComponent {boolean, optional} - A boolean to indicate if the component is a child component. If
 * true, the component will not display the PageHeader and will not show the search bar and will not have padding
 * @param extraItems {Array[Object[{id, label}]], optional} - An array of objects containing id and label of the initial
 * @returns {JSX.Element}
 * @constructor
 */
const PerformanceComparison=({initialObjects, isChildComponent, extraItems, benchmark})=>{
  const {items, commonStartDate, loading, itemColors} = useSelector(state => state.comparison);
  const maxTickers = useSelector(state=>state.appStates.comparison?._maxTickers);
  const [showItemController, setShowItemController] = useState(false);
  const relativeMetrics = useRelativeMetrics({benchmark});
  const screenHeight = useScreenHeight();
    const {
    showSearch,
    setShowSearch,
    selectedExtraItems,
    activeTab,
    setActiveTab,
    handleTickersSubmit,
    handlePortfolioSubmit,
    handleRemoveItem,
    toggleExtraItems,
    reset,
    mapColorsToLabels,
  } = usePerformanceComparison({ initialObjects, benchmark, relativeMetrics });


  if (loading){
      return (
          <Spin><Placeholder text={'Loading Comparison'}/></Spin>
      )
  }
  const navTrailConfig = [
      {label: "Comparison", path: "/comparison"},
      {label: tabKeyToLabelMap?.[activeTab] || "Overview"}
  ]

  return (
        <div>
           <NavTrail items={navTrailConfig} borderBottom={"1px solid #ededed"} extraRight={<Button onClick={()=>setShowItemController(true)}>Item Menu</Button>} />
          {showSearch && (
              <Tabs defaultActiveKey={'tickers'} centered rootClassName={'py-3'}>
                <TabPane tab={'Search Tickers'} key={'tickers'}>
                  <TickerSearch
                      onTickersSubmit={handleTickersSubmit}
                      closeSearch={() => setShowSearch(false)}
                      maxTickers={maxTickers}
                  />
                </TabPane>
                <TabPane tab={'Select Portfolios'} key={'portfolios'}>
                  <PortfolioSelector onSelect={handlePortfolioSubmit} />
                </TabPane>
              </Tabs>
          )}
            {items.length > 0 && (
                <Tabs defaultActiveKey="1" tabPosition={'left'}  onChange={setActiveTab} size={'small'}>
                    <TabPane tab="Overview" key="1">
                        <Card bordered={false}>
                            <ItemController items={items}
                                itemColors={itemColors}
                                onRemove={handleRemoveItem}
                                extraItems={extraItems}
                                onToggleExtraItems={toggleExtraItems}
                                initialSelectedItems={selectedExtraItems}
                                setShowSearch={setShowSearch}
                                reset={reset}
                            />
                          <Divider/>
                          <StockChart dataSeries={prepareOverviewChartData(items, itemColors)} allowChartInteractions={false} height={'65vh'} />
                        </Card>
                    </TabPane>
                    <TabPane tab="Returns" key="2">
                      <Card bordered={false}>
                          <PerformanceHistory items={items} itemColors={mapColorsToLabels}/>
                      </Card>
                    </TabPane>
                    <TabPane tab="Risk" key="3">
                        <div className={'py-4 pr-6'}>
                            <Risk items={items} itemColors={mapColorsToLabels}/>
                        </div>
                    </TabPane>
                    <TabPane tab={'Volatility'} key={'6'}>
                        <Card bordered={false}>
                            <HistoricalVolatility items={items} itemColors={itemColors}/>
                        </Card>
                    </TabPane>
                    <TabPane tab={'Distributions'} key={'7'}>
                        <Card bordered={false}>
                            <ReturnsDistributions items={items} itemColors={itemColors}/>
                        </Card>
                    </TabPane>
                    <TabPane tab={'Metrics'} key={'8'}>
                        <Card bordered={false}>
                            {!relativeMetrics.isCurrent ? (
                              <div>
                                  <Placeholder text={'Click to load metrics'} />
                                  <Divider/>
                                  <Spin>Loading Metrics...</Spin>
                              </div>
                          ) : (
                              <Row gutter={16}>
                                <Col flex={'auto'}>
                                  <GeneralStockChart
                                      height={screenHeight-150}
                                      dataPaths={
                                        items.map(item => {
                                          return {
                                            path: `timeSeries.metrics.${item.id}.${relativeMetrics.metric}.${relativeMetrics.targetSymbol}.${relativeMetrics.window}`,
                                            id: item.id
                                          }
                                        })
                                      }
                                      seriesConfig={items.reduce((config, item) => {
                                        config[item.id] = { dataField: 'value', yScale: 'linear' };
                                        return config;
                                      }, {})}
                                      colorMapping={itemColors}
                                      labelMapping={items.reduce((mapping, item) => {
                                        mapping[item.id] = item.label;
                                        return mapping;
                                      }, {})}
                                      stockChartProps={{
                                        allowChartInteractions: true,
                                      }}
                                  />
                                </Col>
                                <Col flex={'0 0 60px'} style={{background: '#fff', borderRadius: 8, paddingTop: '50px'}}>
                                  <RelativeMetricToolbar
                                      direction={'vertical'}
                                      metric={relativeMetrics.metric}
                                      setMetric={relativeMetrics.setMetric}
                                      targetSymbol={relativeMetrics.targetSymbol}
                                      setTargetSymbol={relativeMetrics.setTargetSymbol}
                                      window={relativeMetrics.window}
                                      setWindow={relativeMetrics.setWindow}
                                  />
                                </Col>
                            </Row>

                          )}
                        </Card>
                    </TabPane>

                  </Tabs>
            )}
          <Modal onCancel={()=>setShowItemController(false)}
                 onOk={()=>setShowItemController(false)}
                 open={showItemController}
                 width={700}
          >
            <ItemMenu items={items}
                      itemColors={itemColors}
                      onRemove={handleRemoveItem}
                      extraItems={extraItems}
                      onToggleExtraItems={toggleExtraItems}
                      initialSelectedItems={selectedExtraItems}
                      setShowSearch={setShowSearch}
                      reset={reset}
            />
          </Modal>
        </div>
    );

}


PerformanceComparison.propTypes = {
  initialObjects: PropTypes.any,
  isChildComponent: PropTypes.bool,
  extraItems: PropTypes.any
}

PerformanceComparison.defaultProps = {initialObjects: null, isChildComponent: false, extraItems: null}

export default PerformanceComparison;
